import type { FeedsClient } from '@stream-io/feeds-client'

import { authorizedFetch } from '../../helpers/authorizedFetch'
import trans from '../../helpers/trans'
import { generateApiV3Url } from '../../helpers/url'
import { FetchResult, FetchResultType } from '../FetchResult'
import {
    ValkTalkFeedMessageResource,
    ValkTalkPost,
    ValkTalkPostFormData,
    ValkTalkPostImage,
    ValkTalkPostsQuery,
    ValkTalkPostsQueryResult,
} from './ValkTalkPost'
import {
    getCurrentTenant,
    getFeedGroupAndId,
    getValkTalkPostsFeedFilter,
    sortValkTalkPostsByNewest,
    VALK_TALK_POSTS_QUERY_LIMIT,
} from './ValkTalkPostHelpers'
import {
    transformActivityToValkTalkPost,
    transformValkTalkPostFormDataToFeedMessageFormData,
    transformValkTalkPostFormDataToUpdateActivityPartialRequest,
} from './ValkTalkPostTransformers'

// Posts are created by Valkplanner, with the current user as author: users only follow the feeds, which doesn't
// allow posting. The other calls go straight to Stream with the current user's Stream token, so Stream decides what
// the user may do (e.g. only edit or delete their own posts). The permission checks in the manager app only decide
// what is shown.

const createErrorResult = (error: unknown, errorKey = 'errors.unknownError'): FetchResult<never, string> => {
    console.error(error)

    return {
        status: 500,
        type: FetchResultType.Error,
        error: trans(errorKey),
    }
}

// Images that are already at Stream keep their url, new images are uploaded to the Stream CDN in the given order.
export const uploadValkTalkPostImagesApiCall = async (
    client: FeedsClient,
    images: ValkTalkPostImage[],
): Promise<FetchResult<string[], string>> => {
    try {
        const imageUrls = await Promise.all(images.map(async image => {
            if (! image.file) {
                return image.url
            }

            const response = await client.uploadImage({ file: image.file })

            if (! response.file) {
                throw new Error(`Stream did not return a url for image "${image.file.name}"`)
            }

            return response.file
        }))

        return {
            status: 200,
            type: FetchResultType.Success,
            data: imageUrls,
        }
    } catch (error: unknown) {
        return createErrorResult(error, 'entities.valkTalkPost.errors.uploadImagesFailed')
    }
}

const getPostValkTalkPostErrorKey = (status: number): string => {
    switch (status) {
        case 403:
            return 'entities.valkTalkPost.errors.postForbidden'
        case 409:
            return 'entities.valkTalkPost.errors.noStreamAccess'
        case 502:
            return 'entities.valkTalkPost.errors.streamConnectionFailed'
        default:
            return 'entities.valkTalkPost.errors.createPostFailed'
    }
}

export const postValkTalkPostApiCall = async (formData: ValkTalkPostFormData): Promise<FetchResult<ValkTalkPost, string>> => {
    try {
        const url = generateApiV3Url({ endpoint: '/stream/feed-messages' })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: transformValkTalkPostFormDataToFeedMessageFormData(formData),
            // Lets the browser set the multipart content type, including its boundary
            disableContentType: true,
        })

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans(getPostValkTalkPostErrorKey(response.status)),
            }
        }

        const { activity }: ValkTalkFeedMessageResource = await response.json()
        const post = transformActivityToValkTalkPost(activity)

        if (! post) {
            throw new Error(`Created activity ${activity.id} is not in a ValkTalk feed`)
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: post,
        }
    } catch (error: unknown) {
        return createErrorResult(error, 'entities.valkTalkPost.errors.createPostFailed')
    }
}

export const patchValkTalkPostApiCall = async (
    client: FeedsClient,
    id: string,
    formData: ValkTalkPostFormData,
    imageUrls: string[],
): Promise<FetchResult<ValkTalkPost, string>> => {
    try {
        const request = transformValkTalkPostFormDataToUpdateActivityPartialRequest(formData, imageUrls)
        const { activity } = await client.updateActivityPartial({ id, ...request })
        const post = transformActivityToValkTalkPost(activity)

        if (! post) {
            throw new Error(`Updated activity ${activity.id} is not in a ValkTalk feed`)
        }

        return {
            status: 200,
            type: FetchResultType.Success,
            data: post,
        }
    } catch (error: unknown) {
        return createErrorResult(error)
    }
}

export const getValkTalkPostsApiCall = async (
    client: FeedsClient,
    query: ValkTalkPostsQuery,
): Promise<FetchResult<ValkTalkPostsQueryResult, string>> => {
    try {
        const filter = getValkTalkPostsFeedFilter(query.month, query.authorId)

        const responses = await Promise.all(query.feedTypes.map(feedType => {
            const [feedGroupId, feedId] = getFeedGroupAndId(feedType, getCurrentTenant())

            return client.getOrCreateFeed({
                feed_group_id: feedGroupId,
                feed_id: feedId,
                filter,
                limit: VALK_TALK_POSTS_QUERY_LIMIT,
                watch: false,
            })
        }))

        const posts = responses
            .flatMap(response => response.activities)
            .map(transformActivityToValkTalkPost)
            .filter((post): post is ValkTalkPost => !! post)

        return {
            status: 200,
            type: FetchResultType.Success,
            data: {
                posts: sortValkTalkPostsByNewest(posts),
                hasMorePosts: responses.some(response => !! response.next),
            },
        }
    } catch (error: unknown) {
        return createErrorResult(error, 'entities.valkTalkPost.errors.getPostsFailed')
    }
}

export const deleteValkTalkPostApiCall = async (
    client: FeedsClient,
    id: string,
): Promise<FetchResult<undefined, string>> => {
    try {
        await client.deleteActivity({ id, hard_delete: true })

        return {
            status: 200,
            type: FetchResultType.Success,
            data: undefined,
        }
    } catch (error: unknown) {
        return createErrorResult(error, 'entities.valkTalkPost.errors.deletePostFailed')
    }
}
